import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import App from "./App.jsx";
import "./index.css";
import "./custom.css";
import { FlowProvider } from "./context/FlowContext.jsx";
import SentryErrorBoundary from "./components/SentryErrorBoundary.jsx";

// Bootstrap CSS
import "bootstrap/dist/css/bootstrap.min.css";

import "bootstrap-icons/font/bootstrap-icons.css";
// Bootstrap Bundle JS
import "bootstrap/dist/js/bootstrap.bundle.min";
// Tostify
import "react-toastify/dist/ReactToastify.min.css";
// Skeleton
import "react-loading-skeleton/dist/skeleton.css";
// Datepicker
import "react-datepicker/dist/react-datepicker.css";
import { StoreProvider } from "./context/StoreContext.jsx";

import * as Sentry from "@sentry/react";
import Clarity from "@microsoft/clarity";
import { initializeReloadTracking, checkReloadStatus } from "./utils/reloadTracker";

// Determine environment
const environment = import.meta.env.VITE_ENV || import.meta.env.MODE || "development";
const isProduction = environment === "prd" || environment === "production";

// Sample rates: 100% in dev, lower in production to reduce costs
const tracesSampleRate = isProduction 
  ? parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0.1") // 10% in production
  : 0; // 0% in development
  
const replaysSessionSampleRate = isProduction
  ? parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE || "0.1") // 10% in production
  : 0; // 0% in development

Sentry.init({
  dsn: "https://ffd0099f761f5a496f0dbd963e99b671@o4508840866283520.ingest.us.sentry.io/4509084060811264",
  environment,
  release: import.meta.env.SENTRY_RELEASE || import.meta.env.npm_package_version || undefined,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate, // Environment-based sample rate
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/api\.flowyogait\.com/,
    /^https:\/\/api\.flowyogaitdev\.com/,
  ],
  // Session Replay
  replaysSessionSampleRate, // Environment-based sample rate
  replaysOnErrorSampleRate: 1.0, // Always capture replays when errors occur
  beforeSend(event) {
    // Filter sensitive data from request body, headers, and extra data
    if (event.request) {
      // Filter sensitive headers
      if (event.request.headers) {
        const sensitiveHeaders = ["authorization", "cookie", "x-api-key"];
        sensitiveHeaders.forEach((header) => {
          if (event.request.headers[header]) {
            event.request.headers[header] = "[Filtered]";
          }
        });
      }

      // Filter sensitive data from body
      if (event.request.data) {
        const sensitiveFields = ["password", "token", "access_token", "refresh_token", "apiKey", "api_key"];
        event.request.data = filterSensitiveData(event.request.data, sensitiveFields);
      }
    }

    // Filter sensitive data from extra context
    if (event.extra) {
      const sensitiveFields = ["password", "token", "access_token", "refresh_token", "apiKey", "api_key"];
      event.extra = filterSensitiveData(event.extra, sensitiveFields);
    }

    return event;
  },
});

function filterSensitiveData(obj, sensitiveFields) {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => filterSensitiveData(item, sensitiveFields));
  }

  const filtered = {};
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveFields.some((field) => lowerKey.includes(field))) {
      filtered[key] = "[Filtered]";
    } else if (typeof value === "object" && value !== null) {
      filtered[key] = filterSensitiveData(value, sensitiveFields);
    } else {
      filtered[key] = value;
    }
  }
  return filtered;
}

// Capture unhandled promise rejections in Sentry with full context
window.addEventListener("unhandledrejection", (event) => {
  Sentry.captureException(event.reason ?? new Error("Unhandled promise rejection"), {
    extra: { promiseRejectionReason: String(event.reason) },
  });
});

// Initialize Microsoft Clarity
Clarity.init("v0x01l1pju");
console.log("[Clarity] Clarity initialized");

// Initialize reload tracking
const isFreshLoad = initializeReloadTracking();
const reloadStatus = checkReloadStatus();

console.log("[ReloadTracker] Status:", {
  isFreshLoad,
  ...reloadStatus,
});

// Check if page hasn't reloaded for 12 hours and force reload if needed
if (reloadStatus.shouldForceReload) {
  console.warn("[ReloadTracker] Page hasn't reloaded for 12+ hours, forcing reload");
  window.location.reload();
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <SentryErrorBoundary>
    <BrowserRouter>
      <FlowProvider>
        <StoreProvider>
          <Routes>
            <Route path="*" element={<App />} />
          </Routes>
        </StoreProvider>
      </FlowProvider>
    </BrowserRouter>
  </SentryErrorBoundary>
);
